import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import PageSkeleton from '@/components/ui/PageSkeleton';
import RoutePrefetcher from '@/components/utils/RoutePrefetcher';
import AdminOnly from '@/components/AdminOnly';
import AppErrorBoundary from '@/components/AppErrorBoundary';
const LandingpageBySlug = lazy(() => import('./pages/Landingpage'));
const RegionAdmin = lazy(() => import('./pages/RegionAdmin'));
const LandingpageAdmin = lazy(() => import('./pages/LandingpageAdmin'));
const DiscountCardAdmin = lazy(() => import('./pages/DiscountCardAdmin'));
const Sponsoring = lazy(() => import('./pages/Sponsoring'));
const AktivitaetEintragen = lazy(() => import('./pages/AktivitaetEintragen'));
const Feedback = lazy(() => import('./pages/Feedback'));
const HaeufigeFragen = lazy(() => import('./pages/HaeufigeFragen'));
const Updates = lazy(() => import('./pages/Updates'));
const UpdateDetail = lazy(() => import('./pages/UpdateDetail'));
const Features = lazy(() => import('./pages/Features'));
const AdminStats = lazy(() => import('./pages/AdminStats'));
const FacilityAdmin = lazy(() => import('./pages/FacilityAdmin'));
const AdminPopularityRanks = lazy(() => import('./pages/AdminPopularityRanks'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail'));
const ArticleRedirect = lazy(() => import('./components/magazine/ArticleRedirect'));
const ActivityDetail = lazy(() => import('./pages/ActivityDetail'));
const MagazineAdmin = lazy(() => import('./pages/MagazineAdmin'));
const AuthorAdmin = lazy(() => import('./pages/AuthorAdmin'));
const MagazineTopicAdmin = lazy(() => import('./pages/MagazineTopicAdmin'));
const About = lazy(() => import('./pages/About'));
const Favorites = lazy(() => import('./pages/Favorites'));
const MyTours = lazy(() => import('./pages/MyTours'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Imprint = lazy(() => import('./pages/Imprint'));

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

// Admin-only pages. These live in pagesConfig, so the loop below wraps them in
// the same gate instead of rendering them openly. Keys match their URL paths.
const ADMIN_PAGES = ['activities-list', 'activity-edit', 'backend-filters', 'smart-filter', 'category-admin'];

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}><Suspense fallback={<PageSkeleton />}>{children}</Suspense></Layout>
  : <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;

const AuthenticatedApp = () => {
  const { authError, navigateToLogin } = useAuth();

  // The app renders immediately — the platform check for app visibility and
  // sign-in runs in the background and only takes over in the rare case that a
  // login really is required.
  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <Routes>
      <Route path="/" element={
        <LayoutWrapper currentPageName={mainPageKey}>
          <MainPage />
        </LayoutWrapper>
      } />
      {Object.entries(Pages).map(([path, Page]) => {
        const page = (
          <LayoutWrapper currentPageName={path}>
            <Page />
          </LayoutWrapper>
        );
        return (
          <Route
            key={path}
            path={`/${path.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()}`}
            element={ADMIN_PAGES.includes(path) ? <AdminOnly>{page}</AdminOnly> : page}
          />
        );
      })}
      <Route path="/region-admin" element={<AdminOnly><LayoutWrapper currentPageName="RegionAdmin"><RegionAdmin /></LayoutWrapper></AdminOnly>} />
      <Route path="/landingpage-admin" element={<AdminOnly><LayoutWrapper currentPageName="LandingpageAdmin"><LandingpageAdmin /></LayoutWrapper></AdminOnly>} />
      <Route path="/discount-card-admin" element={<AdminOnly><LayoutWrapper currentPageName="DiscountCardAdmin"><DiscountCardAdmin /></LayoutWrapper></AdminOnly>} />
      <Route path="/sponsoring" element={<LayoutWrapper currentPageName="Sponsoring"><Sponsoring /></LayoutWrapper>} />
      <Route path="/aktivitaet-eintragen" element={<LayoutWrapper currentPageName="AktivitaetEintragen"><AktivitaetEintragen /></LayoutWrapper>} />
      <Route path="/feedback" element={<LayoutWrapper currentPageName="Feedback"><Feedback /></LayoutWrapper>} />
      <Route path="/haeufige-fragen" element={<LayoutWrapper currentPageName="HaeufigeFragen"><HaeufigeFragen /></LayoutWrapper>} />
      <Route path="/updates" element={<LayoutWrapper currentPageName="Updates"><Updates /></LayoutWrapper>} />
      <Route path="/funktionen" element={<LayoutWrapper currentPageName="Features"><Features /></LayoutWrapper>} />
      <Route path="/ueber-uns" element={<LayoutWrapper currentPageName="About"><About /></LayoutWrapper>} />
      <Route path="/favoriten" element={<LayoutWrapper currentPageName="Favorites"><Favorites /></LayoutWrapper>} />
      <Route path="/meine-touren" element={<LayoutWrapper currentPageName="MyTours"><MyTours /></LayoutWrapper>} />
      <Route path="/datenschutz" element={<LayoutWrapper currentPageName="Privacy"><Privacy /></LayoutWrapper>} />
      <Route path="/impressum" element={<LayoutWrapper currentPageName="Imprint"><Imprint /></LayoutWrapper>} />
      <Route path="/admin-stats" element={<AdminOnly><LayoutWrapper currentPageName="AdminStats"><AdminStats /></LayoutWrapper></AdminOnly>} />
      <Route path="/admin-hub" element={<AdminOnly><LayoutWrapper currentPageName="AdminHub"><AdminPopularityRanks /></LayoutWrapper></AdminOnly>} />
      <Route path="/ausstattung-admin" element={<AdminOnly><LayoutWrapper currentPageName="FacilityAdmin"><FacilityAdmin /></LayoutWrapper></AdminOnly>} />
      <Route path="/magazin/:slug" element={<LayoutWrapper currentPageName="ArticleDetail"><ArticleDetail /></LayoutWrapper>} />
      <Route path="/artikel/:slug" element={<Suspense fallback={<PageSkeleton />}><ArticleRedirect /></Suspense>} />
      <Route path="/magazine-admin" element={<AdminOnly><LayoutWrapper currentPageName="MagazineAdmin"><MagazineAdmin /></LayoutWrapper></AdminOnly>} />
      <Route path="/author-admin" element={<AdminOnly><LayoutWrapper currentPageName="AuthorAdmin"><AuthorAdmin /></LayoutWrapper></AdminOnly>} />
      <Route path="/magazine-topic-admin" element={<AdminOnly><LayoutWrapper currentPageName="MagazineTopicAdmin"><MagazineTopicAdmin /></LayoutWrapper></AdminOnly>} />
      <Route path="/updates/:slug" element={<LayoutWrapper currentPageName="UpdateDetail"><UpdateDetail /></LayoutWrapper>} />
      {/* Activity detail pages: /<category-slug>/<activity-slug>.
          One segment stays the landingpage/collection route below. */}
      <Route path="/:categorySlug/:activitySlug" element={<LayoutWrapper currentPageName="ActivityDetail"><ActivityDetail /></LayoutWrapper>} />
      <Route path="/:slug" element={<LayoutWrapper currentPageName="Landingpage"><LandingpageBySlug /></LayoutWrapper>} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <NavigationTracker />
          <RoutePrefetcher />
          <AppErrorBoundary>
            <AuthenticatedApp />
          </AppErrorBoundary>
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App